import { lazy, Suspense, useState } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  isRouteErrorResponse,
  Navigate,
  Route,
  RouterProvider,
  useRouteError,
} from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import RegisterIndividualPage from './pages/RegisterIndividualPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ManagedOnboardPage from './pages/ManagedOnboardPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDashboardPage from './pages/ProjectDashboardPage';
import ProjectGithubSettingsPage from './pages/ProjectGithubSettingsPage';
import PKGExplorerPage from './pages/PKGExplorerPage';
import SettingsPage from './pages/SettingsPage';
import ProfilePage from './pages/ProfilePage'; // ACT.A6 (#1707) — Certificates section
import SurveyPage from './pages/SurveyPage'; // ACT.A7 (#1708) — End-of-Programme Survey
import SurveyResultsPage from './pages/SurveyResultsPage'; // ACT.A7 (#1708) — survey results
// CFG-IA cluster (#1536) — Settings sub-pages in D1 taxonomy order.
import SettingsInfrastructurePage from './pages/SettingsInfrastructurePage';
import SettingsStoragePage from './pages/SettingsStoragePage';           // CFG-IA.B1 (#1541)
import SettingsGitHubPage from './pages/SettingsGitHubPage';             // CFG-IA.B1 (#1541)
import SettingsDeploymentPage from './pages/SettingsDeploymentPage'; // CFG-IA.A2 (#1537)
import SettingsAgentConfigPage from './pages/SettingsAgentConfigPage'; // CFG-IA.A3 (#1538)
import SettingsEstimationPage from './pages/SettingsEstimationPage';   // CFG-IA.B1 (#1541)
// ONB.C3 (A.5) — BYOIWizardPage deleted; /settings/byoi shim → /settings/storage (B1).
import SettingsBYOIRedirect from './pages/SettingsBYOIRedirect';
import SettingsDriveAuthPage from './pages/SettingsDriveAuthPage';
import AuthorizeGDrivePage from './pages/AuthorizeGDrivePage';
import OAuthPopupBouncePage from './pages/OAuthPopupBouncePage';
import ArtefactDetailPage from './pages/ArtefactDetailPage';
import ArtefactsPage from './pages/ArtefactsPage';
import ImportPage from './pages/ImportPage';
import ChatPage from './pages/ChatPage';
import AmendmentsPage from './pages/AmendmentsPage';
import ChangeOrdersPage from './pages/ChangeOrdersPage';
import DecisionsPage from './pages/DecisionsPage';
import BoardPage from './pages/BoardPage';
import BacklogPage from './pages/BacklogPage';
import RisksPage from './pages/RisksPage';
import NfrsPage from './pages/NfrsPage';
import OpenQuestionsPage from './pages/OpenQuestionsPage'; // #2057 item 2
import RefinementSuggestionsPage from './pages/RefinementSuggestionsPage'; // Piece 4c (#2151)
import TraceabilityPage from './pages/TraceabilityPage';
import CalibrationReportPage from './pages/CalibrationReportPage';
import OnboardingPage from './pages/OnboardingPage';
import ClientOnboarding from './pages/ClientOnboarding';
import ClientAcceptInvitePage from './pages/ClientAcceptInvitePage'; // #2203 — magic-link landing, distinct from the authenticated /client/onboarding step
import SettingsOrganisationPage from './pages/SettingsOrganisationPage';
import CustodyTransferPage from './pages/CustodyTransferPage'; // CT.A5 (#2535)
import ClientApprovalsSettingsPage from './pages/ClientApprovalsSettingsPage'; // #3185 APPROVE.2 2c
import SettingsTenantBrandingPage from './pages/SettingsTenantBrandingPage';
import SettingsCertificateInstitutionPage from './pages/SettingsCertificateInstitutionPage';
import AgentModelSettingsPage from './pages/AgentModelSettingsPage';
import QAPolicySettingsPage from './pages/QAPolicySettingsPage';
import SettingsPlatformAccessLogPage from './pages/SettingsPlatformAccessLogPage';
import SettingsPkgHealthPage from './pages/SettingsPkgHealthPage'; // PKGV.D4 (#804)
import Dashboard from './pages/Dashboard';
import WorkbasketPage from './pages/WorkbasketPage'; // #2762 — Delivery-tier workbasket
import AcceptInvitePage from './pages/AcceptInvitePage';
import SetupAcceptPage from './pages/SetupAcceptPage';
import AcademicAcceptPage from './pages/AcademicAcceptPage';
import SetupWizardPage from './pages/SetupWizardPage';
import SetupSubWizardPlaceholder from './pages/SetupSubWizardPlaceholder';
import LinkExpiredPage from './pages/LinkExpiredPage';
import ActionLandingPage from './pages/ActionLandingPage';
import ActionPage from './pages/ActionPage';
import ClientDashboard from './pages/ClientDashboard';
import { clientApprovalRoutes } from './routes/clientApprovalRoutes'; // #3187 APPROVE.4
import ClientProjectPicker from './pages/ClientProjectPicker'; // #2220 — multi-project CLIENT landing
import ProjectMemberDashboard from './pages/ProjectMemberDashboard'; // #2331 — project_members-only landing
import CohortDashboardPage from './pages/CohortDashboardPage';
import ClientRouteGuard from './components/ClientRouteGuard';
import { TierUpgradeBanner } from './components/TierUpgradeBanner';
import { TokenExhaustionOverlay } from './components/TokenExhaustionOverlay';
import PartnerPortalPage from './pages/PartnerPortalPage';
import PartnerOnboardingWizardPage from './pages/partner/PartnerOnboardingWizardPage';
import ClientOnboardingWizardPage from './pages/ClientOnboardingWizardPage';
import ClientManagementPage from './pages/ClientManagementPage';
import ClientDetailPage from './pages/ClientDetailPage';
import ApprovalConfigPage from './pages/ApprovalConfigPage';
import PartnerTermsPage from './pages/PartnerTermsPage';
import PlatformAdminPage from './pages/PlatformAdminPage';
import PlatformAdminGuard from './components/PlatformAdminGuard';
import PlatformMfaSetupPage from './pages/PlatformMfaSetupPage';
// Sprint INTADMIN INTADMIN.B1 (#2110) — internal admin area.
import InternalAdminGuard from './components/InternalAdminGuard';
import InternalAdminLayout from './components/internal/InternalAdminLayout';
import InternalPricingPage from './pages/internal/InternalPricingPage';
// Sprint TENANTS TENANTS.B1 (#2337) — tenant list UI.
import InternalTenantsPage from './pages/internal/InternalTenantsPage';
import OAuthCallbackPage from './pages/OAuthCallbackPage';
import IndividualOnboardingPage from './pages/IndividualOnboardingPage';
import SMEOnboardingPage from './pages/SMEOnboardingPage';
// Sprint ACAD ACT.A8 (#1709) — Academic Onboarding Flows.
import StudentOnboardingPage from './pages/StudentOnboardingPage';
import FacultyOnboardingPage from './pages/FacultyOnboardingPage';
import AcademicAdminOnboardingPage from './pages/AcademicAdminOnboardingPage';
import EmailVerificationPage from './pages/EmailVerificationPage';
import BillingPage from './pages/BillingPage';
import ProjectCodebasePage from './pages/ProjectCodebasePage';
import BrownfieldOnboardingPage from './pages/BrownfieldOnboardingPage';
import ProjectLayout from './components/ProjectLayout';
import ProjectChatLayout from './components/ProjectChatLayout';
import { FEATURE_FLAGS } from './config/featureFlags';
// Dev-only visual harness for HelpPopover (Stage 1). Loaded via a
// dynamic import that is only evaluated when `import.meta.env.DEV` is
// truthy. Static analysis can drop the entire module from production
// bundles regardless of side-effects in the page module — a static
// `import` would not have that guarantee. The lazy chunk is wrapped in
// the `<Route>` element below so it only loads when the dev-only route
// is hit. `null` in prod means the route below is skipped entirely.
const HelpPopoverDevPage = import.meta.env.DEV
  ? lazy(() => import('./pages/HelpPopoverDevPage'))
  : null;

// SETUX.X5 (#3765, Q2) — the root route's errorElement. An unmatched URL is
// a 404 route error in a data router; rendering nothing keeps today's blank
// page (#2494; no catch-all route). Any other error, such as a page that
// throws while rendering, is rethrown so it reaches Sentry.ErrorBoundary and
// ErrorFallback in main.tsx, as it did under <BrowserRouter>, instead of
// React Router's default error screen.
function RootRouteError() {
  const error = useRouteError();
  if (isRouteErrorResponse(error) && error.status === 404) return null;
  throw error;
}

// The route table. SETUX.X5 moved it here from inside <Routes>, unchanged
// except for indentation. App puts it under one pathless root route that
// carries the errorElement above.
const routeTree = (
  <>
    <Route path="/" element={<Navigate to="/projects" replace />} />
    <Route
      path="/dashboard"
      element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      }
    />
    {/* #2762 — Delivery-tier workbasket. Top-level, like /dashboard —
        not nested under /projects/:projectId (it spans projects). The
        page itself bounces a CLIENT-tier caller to /projects on the
        backend's 403 (see WorkbasketPage.tsx). */}
    <Route
      path="/workbasket"
      element={
        <ProtectedRoute>
          <WorkbasketPage />
        </ProtectedRoute>
      }
    />
    <Route path="/login" element={<LoginPage />} />
    {/* ONB-FIX.D9 (#1081) — password reset flow. Public routes; user
        is locked out by definition, so no ProtectedRoute wrapper.
        /reset-password reads the raw token from `?token=` and POSTs
        the new password. /forgot-password takes an email and triggers
        the no-enumeration email send. */}
    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
    <Route path="/reset-password" element={<ResetPasswordPage />} />
    {/* APPROVE.1a (#3183) / #3190 — partner-managed client onboarding link (public). */}
    <Route path="/onboard/set-password" element={<ManagedOnboardPage />} />
    <Route path="/link-expired" element={<LinkExpiredPage />} />
    {/* #3248 PR 2 — the emailed magic link: /action#t=<token>. No auth
        guard: it reads the fragment, strips it, and redeems only on the
        Continue click (POST /action/redeem). /action-landing stays: it
        is the redirectTo the redeem response points at. */}
    <Route path="/action" element={<ActionPage />} />
    <Route path="/action-landing" element={<ActionLandingPage />} />
    <Route path="/register" element={<RegisterPage />} />
    <Route path="/register/individual" element={<RegisterIndividualPage />} />
    <Route path="/verify-email" element={<EmailVerificationPage />} />
    <Route
      path="/projects"
      element={
        <ProtectedRoute>
          <ProjectsPage />
        </ProtectedRoute>
      }
    />
    {/* #1807 — `new` is not a project id. Without this, /projects/new
        matched /projects/:projectId below and the project dashboard
        requested the API with `new` as the id. */}
    <Route path="/projects/new" element={<Navigate to="/projects?create=1" replace />} />
    {/* Sprint A.9 — nested project layout. ProjectLayout wraps every
        /projects/:projectId/* route with the shared Sidebar +
        (forthcoming) SDLC phase stepper. The parent path uses
        :projectId; the 4 pages that historically destructured `id`
        alias on destructure (`{ projectId: id }`) to preserve their
        internal naming. URL strings are unchanged (path-pattern
        param names are label-agnostic). */}
    {/* Chat gets its own bounded-height layout (T-layout-4, #1168's
        V1(c) fallback) — see ProjectChatLayout.tsx. Same URL as before;
        it is deliberately NOT a child of ProjectLayout below. */}
    <Route
      path="/projects/:projectId/chat"
      element={
        <ProtectedRoute>
          <ProjectChatLayout />
        </ProtectedRoute>
      }
    >
      <Route index element={<ChatPage />} />
    </Route>
    <Route
      path="/projects/:projectId"
      element={
        <ProtectedRoute>
          <ProjectLayout />
        </ProtectedRoute>
      }
    >
      <Route index element={<ProjectDashboardPage />} />
      <Route path="import" element={<ImportPage />} />
      <Route path="approval-config" element={<ApprovalConfigPage />} />
      <Route path="settings/github" element={<ProjectGithubSettingsPage />} />
      <Route path="codebase" element={<ProjectCodebasePage />} />
      <Route path="amendments" element={<AmendmentsPage />} />
      <Route path="change-orders" element={<ChangeOrdersPage />} />
      <Route path="decisions" element={<DecisionsPage />} />
      <Route path="board" element={<BoardPage />} />
      <Route path="backlog" element={<BacklogPage />} />
      <Route path="calibration" element={<CalibrationReportPage />} />
      <Route path="risks" element={<RisksPage />} />
      <Route path="nfrs" element={<NfrsPage />} />
      <Route path="open-questions" element={<OpenQuestionsPage />} />
      <Route path="refinement-suggestions" element={<RefinementSuggestionsPage />} />
      <Route path="traceability" element={<TraceabilityPage />} />
      {/* PKGV.B2 (#796) — feature-flag-gated; no NotFound page
          exists in this app's routing (checked: no catch-all route
          anywhere in App.tsx), so "disabled" redirects to the
          project dashboard rather than inventing a novel 404 UI —
          same graceful-degradation pattern ProjectLayout itself
          uses for its own invalid-state case above. */}
      <Route
        path="pkg-explorer"
        element={FEATURE_FLAGS.PKG_EXPLORER_ENABLED ? <PKGExplorerPage /> : <Navigate to=".." replace />}
      />
    </Route>
    {/* ONB.C3 (A.5) — deprecated GCP-manual BYOI wizard → redirect. */}
    <Route
      path="/settings/byoi"
      element={
        <ProtectedRoute>
          <SettingsBYOIRedirect />
        </ProtectedRoute>
      }
    />
    {/* ONB.C3 (A.5) — Settings Drive re-auth surface. */}
    <Route
      path="/settings/integrations/gdrive"
      element={
        <ProtectedRoute>
          <SettingsDriveAuthPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/settings"
      element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      }
    />
    {/* ACT.A6 (#1707) — student profile page, Certificates section. */}
    <Route
      path="/profile"
      element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      }
    />
    {/* ACT.A7 (#1708) — End-of-Programme Survey. Standalone routes,
        deliberately NOT nested under /projects/:id — the AC requires
        a dedicated survey page outside any phase-agent session (Q1 —
        there is no conversational "Retrospective Agent" to nest
        inside anyway). */}
    <Route
      path="/cohorts/:cohortId/survey"
      element={
        <ProtectedRoute>
          <SurveyPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/cohorts/:cohortId/survey/results"
      element={
        <ProtectedRoute>
          <SurveyResultsPage />
        </ProtectedRoute>
      }
    />
    {/* ── CFG-IA route anchor (#1536, A1 establishes) ──────────────────
        Settings sub-page routes in D1 taxonomy order:
          Infrastructure → Storage → GitHub → Deployment →
          Quality & QA → Agents → Estimation.
        A2/A3/A4/B1 insert their <Route> here, IN ORDER. Keep contiguous. */}
    <Route
      path="/settings/infrastructure"
      element={
        <ProtectedRoute>
          <SettingsInfrastructurePage />
        </ProtectedRoute>
      }
    />
    {/* CFG-IA.B1 (#1541) — Storage provider + Drive auth surface. */}
    <Route
      path="/settings/storage"
      element={
        <ProtectedRoute>
          <SettingsStoragePage />
        </ProtectedRoute>
      }
    />
    {/* CFG-IA.B1 (#1541) — GitHub connection (relocated from inline SettingsPage). */}
    <Route
      path="/settings/github"
      element={
        <ProtectedRoute>
          <SettingsGitHubPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/settings/deployment"
      element={
        <ProtectedRoute>
          <SettingsDeploymentPage />
        </ProtectedRoute>
      }
    />
    {/* CFG-IA.A3 (#1538) — Agents. */}
    <Route
      path="/settings/agents"
      element={
        <ProtectedRoute>
          <SettingsAgentConfigPage />
        </ProtectedRoute>
      }
    />
    {/* CFG-IA.B1 (#1541) — Estimation. Terminal taxonomy position. */}
    <Route
      path="/settings/estimation"
      element={
        <ProtectedRoute>
          <SettingsEstimationPage />
        </ProtectedRoute>
      }
    />
    {/* ── end CFG-IA route anchor ─────────────────────────────────── */}
    <Route
      path="/settings/organisation"
      element={
        <ProtectedRoute>
          <SettingsOrganisationPage />
        </ProtectedRoute>
      }
    />
    {/* CT.A5 (#2535) — custody transfer workflow (project selection,
        group review, confirmation, history). TENANT_ADMIN only; the
        page itself fail-closed redirects non-admins. */}
    <Route
      path="/settings/custody-transfers"
      element={
        <ProtectedRoute>
          <CustodyTransferPage />
        </ProtectedRoute>
      }
    />
    {/* #3185 APPROVE.2 2c — the workspace client approval matrix. Admin
        rank only; the page itself redirects non-admins to /settings. */}
    <Route
      path="/settings/client-approvals"
      element={
        <ProtectedRoute>
          <ClientApprovalsSettingsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/settings/branding"
      element={
        <ProtectedRoute>
          <SettingsTenantBrandingPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/settings/certificate-institution"
      element={
        <ProtectedRoute>
          <SettingsCertificateInstitutionPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/settings/agent-models"
      element={
        <ProtectedRoute>
          <AgentModelSettingsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/settings/qa-policy"
      element={
        <ProtectedRoute>
          <QAPolicySettingsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/settings/security/platform-access-log"
      element={
        <ProtectedRoute>
          <SettingsPlatformAccessLogPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/settings/pkg-health"
      element={
        <ProtectedRoute>
          <SettingsPkgHealthPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/artefacts"
      element={
        <ProtectedRoute>
          <ArtefactsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/artefacts/:id"
      element={
        <ProtectedRoute>
          <ArtefactDetailPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/onboarding"
      element={
        <ProtectedRoute>
          <OnboardingPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/client/onboarding"
      element={
        <ProtectedRoute>
          <ClientOnboarding />
        </ProtectedRoute>
      }
    />
    <Route
      path="/accept-invite"
      element={<AcceptInvitePage />}
    />
    {/* ONB.B2-FIX.D1 — Tech Lead /setup/accept magic-link landing.
        Public route; auth check is inside the page so unauthed
        visitors can be redirected to /login with the token-bearing
        ?redirect= URL (round-trip honored by LoginPage in this PR). */}
    <Route
      path="/setup/accept"
      element={<SetupAcceptPage />}
    />
    {/* #1784 — academic-invitation /academic/accept magic-link landing.
        Public route, same reasoning as /setup/accept above; URL is
        already baked into academic-tenant.service.ts's buildInviteUrl. */}
    <Route
      path="/academic/accept"
      element={<AcademicAcceptPage />}
    />
    {/* ONB.C1 (A.3) — focused Setup Wizard (Tech-Lead-gated server-side).
        Static /setup/accept above outranks these. The two sub-wizard
        placeholders are replaced by A.4 (GitHub) and A.5 (Drive). */}
    <Route
      path="/setup"
      element={
        <ProtectedRoute>
          <SetupWizardPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/setup/connect-github"
      element={
        <ProtectedRoute>
          <SetupSubWizardPlaceholder />
        </ProtectedRoute>
      }
    />
    {/* ONB.C3 (A.5) — Drive OAuth sub-wizard replaces the A.3 placeholder. */}
    <Route
      path="/setup/authorize-gdrive"
      element={
        <ProtectedRoute>
          <AuthorizeGDrivePage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/setup/oauth-bounce"
      element={<OAuthPopupBouncePage />}
    />
    <Route
      path="/client/accept-invite"
      element={<ClientAcceptInvitePage />}
    />
    <Route
      path="/client/dashboard"
      element={
        <ProtectedRoute>
          <ClientDashboard />
        </ProtectedRoute>
      }
    />
    {/* #3187 APPROVE.4 — the approval version view and G2's landing. Dark
        until CLIENT_APPROVAL_ENFORCEMENT_LIVE (render nothing, as an
        unmatched URL does); the version view admits a full login or an
        APPROVE action session for its artefact (ActionRoute, #3248). */}
    {clientApprovalRoutes}
    <Route
      path="/client/select-project"
      element={
        <ProtectedRoute>
          <ClientProjectPicker />
        </ProtectedRoute>
      }
    />
    {/* #2331 — front door for project_members-only accounts (no
        tenant_members row, not a project_stakeholders row either).
        Auth-only at the route level, same convention as every other
        role-specific page in this file; access to the linked routes
        is enforced server-side (assertArtefactProjectOrTenantAccess). */}
    <Route
      path="/member/dashboard"
      element={
        <ProtectedRoute>
          <ProjectMemberDashboard />
        </ProtectedRoute>
      }
    />
    <Route
      path="/client-onboarding"
      element={<ClientOnboardingWizardPage />}
    />
    {/* ACT.A5 (#1706) — Faculty Supervisor Cohort Dashboard. Auth-only
        at the route level (matches every other role-specific page in
        this file); FACULTY_SUPERVISOR/TENANT_ADMIN-only enforcement
        happens server-side (routes/cohort-dashboard.ts). */}
    <Route
      path="/academic/cohorts/:cohortId/dashboard"
      element={
        <ProtectedRoute>
          <CohortDashboardPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/partner"
      element={
        <ProtectedRoute>
          <PartnerPortalPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/billing"
      element={
        <ProtectedRoute>
          <BillingPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/settings/billing"
      element={
        <ProtectedRoute>
          <BillingPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/partner/billing"
      element={
        <ProtectedRoute>
          <BillingPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/partner/clients"
      element={
        <ProtectedRoute>
          <ClientManagementPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/partner/clients/:id"
      element={
        <ProtectedRoute>
          <ClientDetailPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/partner/onboarding"
      element={
        <ProtectedRoute>
          <PartnerOnboardingWizardPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/admin"
      element={
        <PlatformAdminGuard>
          <PlatformAdminPage />
        </PlatformAdminGuard>
      }
    />
    {/* Alias — typing /platform/admin previously rendered blank
        because no route matched. Mount the same page here until
        issue #761 lands the proper PA frontend pages. */}
    <Route
      path="/platform/admin"
      element={
        <PlatformAdminGuard>
          <PlatformAdminPage />
        </PlatformAdminGuard>
      }
    />
    {/* Sprint INTADMIN INTADMIN.B1 (#2110) — internal admin area.
        Deliberately separate from the /admin and /platform/admin
        stopgap pages above, which stay untouched (that's #761's
        scope). Path is /internal, matching the /api/internal/*
        prefix the sprint already fixed.

        Every route here is wrapped in InternalAdminGuard, which
        probes GET /api/internal/session and redirects silently on
        denial — no error UI, no hint the route exists. The
        authoritative gate is the server's, on /api/internal/*.

        /internal itself redirects to the one panel this sprint
        ships rather than rendering an empty shell. The redirect
        sits INSIDE the guard so a non-admin never learns that
        /internal/pricing exists either. */}
    <Route
      path="/internal"
      element={
        <InternalAdminGuard>
          <Navigate to="/internal/pricing" replace />
        </InternalAdminGuard>
      }
    />
    <Route
      path="/internal/pricing"
      element={
        <InternalAdminGuard>
          <InternalAdminLayout>
            <InternalPricingPage />
          </InternalAdminLayout>
        </InternalAdminGuard>
      }
    />
    {/* TENANTS.B1 (#2337) — tenant list UI. The :tenantId variant
        renders the exact same page; InternalTenantsPage reads the
        param itself (via useParams) to expand/pin that tenant's row —
        see that file's header for why a deep link can't just rely on
        the tenant landing on the current page/filter/sort. */}
    <Route
      path="/internal/tenants"
      element={
        <InternalAdminGuard>
          <InternalAdminLayout>
            <InternalTenantsPage />
          </InternalAdminLayout>
        </InternalAdminGuard>
      }
    />
    <Route
      path="/internal/tenants/:tenantId"
      element={
        <InternalAdminGuard>
          <InternalAdminLayout>
            <InternalTenantsPage />
          </InternalAdminLayout>
        </InternalAdminGuard>
      }
    />
    {/* CFG.PA.A3.UI (issue #758) — MFA enrollment for platform
        admins. Standard ProtectedRoute (must be logged in via the
        normal session) — the page calls GET /auth/platform/mfa/status
        and the backend rejects callers who aren't a platform admin.
        We deliberately don't gate on PlatformAdminGuard here, which
        calls /api/admin/partners and would need a PA-claim JWT —
        that's the chicken-and-egg the backend is designed to avoid. */}
    <Route
      path="/platform/mfa/setup"
      element={
        <ProtectedRoute>
          <PlatformMfaSetupPage />
        </ProtectedRoute>
      }
    />
    <Route path="/auth/callback" element={<OAuthCallbackPage />} />
    <Route
      path="/onboarding/individual"
      element={
        <ProtectedRoute>
          <IndividualOnboardingPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/onboarding/sme"
      element={
        <ProtectedRoute>
          <SMEOnboardingPage />
        </ProtectedRoute>
      }
    />
    {/* Sprint ACAD ACT.A8 (#1709) — Academic Onboarding Flows. */}
    <Route
      path="/onboarding/student"
      element={
        <ProtectedRoute>
          <StudentOnboardingPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/onboarding/faculty-supervisor"
      element={
        <ProtectedRoute>
          <FacultyOnboardingPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/onboarding/academic-admin"
      element={
        <ProtectedRoute>
          <AcademicAdminOnboardingPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/onboard"
      element={
        <ProtectedRoute>
          <BrownfieldOnboardingPage />
        </ProtectedRoute>
      }
    />
    {/* Public — clients and partners may need to read the terms before activating */}
    <Route path="/terms/partner" element={<PartnerTermsPage />} />
    {/* Dev-only HelpPopover visual harness — Stage 1 of the
        in-product help rollout. The lazy import above resolves
        to `null` in production so this route is absent there.
        Remove or migrate once Stage 2 wires the component into
        real surfaces. */}
    {HelpPopoverDevPage && (
      <Route
        path="/dev/help-popover-test"
        element={
          <Suspense fallback={null}>
            <HelpPopoverDevPage />
          </Suspense>
        }
      />
    )}
  </>
);

export default function App() {
  // SETUX.X5 (#3765, Q3) — one router per App mount, created in a useState
  // initialiser: not at module scope, because tests set the URL before
  // rendering <App/>, and never disposed. createBrowserRouter subscribes to
  // popstate when it is created, so every extra router leaks one popstate
  // listener: StrictMode's second initialiser call in dev, and one per
  // render in tests. Production mounts App once.
  const [router] = useState(() =>
    createBrowserRouter([
      { id: 'root', errorElement: <RootRouteError />, children: createRoutesFromElements(routeTree) },
    ]),
  );
  return (
    <ThemeProvider>
      <TierUpgradeBanner />
      <TokenExhaustionOverlay />
      <AuthProvider>
        <TenantProvider>
          <RouterProvider router={router} />
        </TenantProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
